import React from 'react';
import { useNavigate } from 'react-router-dom';
import './MyPage.css';
import AlarmSettings from './mypage/AlarmSettings';
import UserInfo from './mypage/UserInfo';
import Notices from './mypage/Notices';
import Events from './mypage/Events';
import Terms from './mypage/Terms';
import Products from './mypage/Products';
import Partners from './mypage/Partners';
import CompanyInfo from './mypage/CompanyInfo';
import { useAuth } from '../contexts/AuthContext';
import { useAlert } from '../contexts/AlertContext';
import { supabase } from '../lib/supabase';
import productIcon from '../assets/images/product_icon.svg';
import alarmIcon from '../assets/images/alarm_icon.svg';
import administratorIcon from '../assets/images/administrator_icon.svg';
import supportIcon from '../assets/images/support_icon.svg';

export type SubPageType = 'main' | 'products' | 'alarm' | 'userinfo' | 'notices' | 'events' | 'terms' | 'privacy' | 'location' | 'partners' | 'companyinfo';

export interface MyPageHandle {
  canGoBack: () => boolean;
  goBack: () => void;
}

export interface MyPageProps {
  initialPage?: SubPageType;
  onPageChange?: (isSubPage: boolean) => void;
}

const MyPage = React.forwardRef<MyPageHandle, MyPageProps>(({ initialPage = 'main', onPageChange }, ref) => {
  const navigate = useNavigate();
  const { customer, logout } = useAuth();
  const { showAlert } = useAlert();

  // URL에서 받은 initialPage를 직접 사용 (상태 대신)
  const currentPage = initialPage;

  // 뒤로가기 (브라우저 히스토리 사용)
  const handleBackToMain = () => {
    navigate(-1);
  };

  // 서브페이지로 이동 (URL 기반)
  const navigateToSubPage = (page: SubPageType) => {
    navigate(`/main/mypage/${page}`);
  };

  // currentPage 변경 시 부모 컴포넌트에 알림
  React.useEffect(() => {
    console.log('MyPage currentPage:', currentPage, 'isSubPage:', currentPage !== 'main');
    onPageChange?.(currentPage !== 'main');
  }, [currentPage, onPageChange]);

  // 상담 요청
  const handleConsultRequest = async () => {
    if (!customer) {
      showAlert('로그인이 필요합니다.');
      return;
    }

    try {
      // 채팅방 생성 (일반 상담)
      const { data: chatRoom, error } = await supabase
        .from('chat_rooms')
        .insert([{
          customer_id: customer.id,
          status: 'waiting',
          product_type: 'general'
        }])
        .select()
        .single();

      if (error) throw error;

      // 상담원 자동 메시지 전송 (상담원이 보낸 것처럼 표시)
      const welcomeMessage = '상담 요청이 정상적으로 접수되었습니다. 잠시 후 전문 상담원이 배정됩니다.';

      const { error: messageError } = await supabase.from('messages').insert([{
        room_id: chatRoom.id,
        sender_id: null, // 시스템 메시지는 sender_id를 null로
        message_type: 'text',
        content: welcomeMessage,
        is_read: true  // 고객이 바로 채팅방으로 이동하므로 읽음 처리
      }]);

      if (messageError) {
        console.error('Error sending initial message:', messageError);
      }

      // 채팅방으로 이동
      console.log('🚀 Navigating to chat room:', chatRoom.id);
      navigate('/main/chat/' + chatRoom.id);
      console.log('🚀 Navigate called');
    } catch (error: any) {
      console.error('Error creating chat room:', error);
      showAlert('상담 요청 중 오류가 발생했습니다: ' + (error?.message || '알 수 없는 오류'));
    }
  };

  // ref로 메서드 노출
  React.useImperativeHandle(ref, () => ({
    canGoBack: () => currentPage !== 'main',
    goBack: () => {
      if (currentPage !== 'main') {
        navigate(-1);
      }
    }
  }));

  const handleLogout = async () => {
    await logout();
    // 페이지 리로드로 모든 상태 초기화 (PKCE 세션 포함)
    window.location.href = '/login';
  };

  if (currentPage === 'products') {
    return <Products onBack={handleBackToMain} />;
  }

  if (currentPage === 'alarm') {
    return <AlarmSettings onBack={handleBackToMain} />;
  }

  if (currentPage === 'userinfo') {
    return <UserInfo onBack={handleBackToMain} />;
  }

  if (currentPage === 'notices') {
    return <Notices onBack={handleBackToMain} />;
  }

  if (currentPage === 'events') {
    return <Events onBack={handleBackToMain} />;
  }

  if (currentPage === 'terms') {
    return <Terms onBack={handleBackToMain} />;
  }

  if (currentPage === 'privacy') {
    return <Terms onBack={handleBackToMain} />;
  }

  if (currentPage === 'location') {
    return <Terms onBack={handleBackToMain} />;
  }

  if (currentPage === 'partners') {
    return <Partners onBack={handleBackToMain} />;
  }

  if (currentPage === 'companyinfo') {
    return <CompanyInfo onBack={handleBackToMain} />;
  }

  return (
    <div className="mypage-container">
      <div className="mypage-content">
        <h1 className="mypage-title">마이페이지</h1>

        <div className="mypage-greeting">
          <p className="greeting-nickname">{customer?.nickname || '게스트'}님, </p>
          <p className="greeting-message">오늘도 좋은 하루 되세요.</p>
        </div>

        <div className="mypage-quick-menu">
          <button className="quick-menu-item" onClick={() => navigateToSubPage('products')}>
            <div className="quick-menu-icon">
              <img src={productIcon} alt="가입한상품" width="40" height="40" />
            </div>
            <span>가입한상품</span>
          </button>
          <button className="quick-menu-item" onClick={() => navigateToSubPage('alarm')}>
            <div className="quick-menu-icon">
              <img src={alarmIcon} alt="알림설정" width="40" height="40" />
            </div>
            <span>알림설정</span>
          </button>
          <button className="quick-menu-item" onClick={() => navigateToSubPage('userinfo')}>
            <div className="quick-menu-icon">
              <img src={administratorIcon} alt="회원정보" width="40" height="40" />
            </div>
            <span>회원정보</span>
          </button>
          <button className="quick-menu-item" onClick={handleConsultRequest}>
            <div className="quick-menu-icon">
              <img src={supportIcon} alt="상담요청" width="40" height="40" />
            </div>
            <span>상담요청</span>
          </button>
        </div>

        <div className="mypage-divider"></div>

        <nav className="mypage-menu">
          <button className="menu-item" onClick={() => navigateToSubPage('partners')}>
            <span>협력사</span>
            <span className="arrow">›</span>
          </button>

          <div className="mypage-divider"></div>

          <button className="menu-item" onClick={() => navigateToSubPage('notices')}>
            <span>공지사항</span>
            <span className="arrow">›</span>
          </button>

          <button className="menu-item" onClick={() => navigateToSubPage('events')}>
            <span>이벤트/프로모션</span>
            <span className="arrow">›</span>
          </button>

          <div className="mypage-divider"></div>

          <button className="menu-item" onClick={() => navigateToSubPage('terms')}>
            <span>약관정보</span>
            <span className="arrow">›</span>
          </button>

          <button className="menu-item" onClick={() => navigateToSubPage('privacy')}>
            <span>개인정보처리방침</span>
            <span className="arrow">›</span>
          </button>

          <button className="menu-item" onClick={() => navigateToSubPage('location')}>
            <span>위치기반서비스 이용약관</span>
            <span className="arrow">›</span>
          </button>

          <div className="mypage-divider"></div>

          <button className="menu-item" onClick={() => navigateToSubPage('companyinfo')}>
            <span>회사 정보</span>
            <span className="arrow">›</span>
          </button>

          <div className="mypage-divider mypage-divider-large"></div>

          <button className="menu-item logout" onClick={handleLogout}>
            <span>로그아웃</span>
          </button>
        </nav>
      </div>
    </div>
  );
});

MyPage.displayName = 'MyPage';

export default MyPage;
